import { useCallback, useContext, useEffect, useRef } from "react"
import { useBlocker, type ShouldBlockFn } from "@tanstack/react-router"

import { ApplicationContext } from "./application-context"

export type LeaveGuard = () => boolean | Promise<boolean>

export interface UseLeaveGuardOptions {
  /** Whether the guard participates in client-side navigation. */
  readonly enabled?: boolean
  /** Limits which client-side navigations actually require the guard. */
  readonly shouldGuardNavigation?: (navigation: Parameters<ShouldBlockFn>[0]) => boolean
  /**
   * Enables the browser's native before-unload prompt while this guard is
   * enabled. The browser cannot await an async guard during unload, so callers
   * should normally pair this with an `enabled` value derived from dirty state.
   */
  readonly enableBeforeUnload?: boolean
}

/**
 * Blocks TanStack Router navigation when the consumer-owned leave guard does
 * not allow the current page to be left. The framework deliberately does not
 * own dirty state or confirmation UI.
 */
export function useLeaveGuard(guard: LeaveGuard, options: UseLeaveGuardOptions = {}) {
  const enabled = options.enabled ?? true
  const guardRef = useRef(guard)
  guardRef.current = guard
  const shouldGuardNavigationRef = useRef(options.shouldGuardNavigation)
  shouldGuardNavigationRef.current = options.shouldGuardNavigation
  const shouldBlockFn = useCallback(async (navigation: Parameters<ShouldBlockFn>[0]) => {
    if (shouldGuardNavigationRef.current?.(navigation) === false) return false
    return !(await guardRef.current())
  }, [])

  useBlocker({
    shouldBlockFn,
    disabled: !enabled,
    enableBeforeUnload: options.enableBeforeUnload ?? false,
  })
}

/**
 * Lets `application.signOut()` consult this guard before it clears the session, for state that
 * sign-out would otherwise discard. Document leave guards already take part through their workspace.
 * Outside ApplicationProvider the guard is not registered.
 */
export function useSignOutGuard(guard: LeaveGuard, options: { readonly enabled?: boolean } = {}) {
  const application = useContext(ApplicationContext)
  const enabled = options.enabled ?? true
  const guardRef = useRef(guard)
  guardRef.current = guard

  useEffect(() => {
    if (!enabled || application == null) return
    return application.registerSignOutGuard(() => guardRef.current())
  }, [application, enabled])
}
